(() => {
  const videoSelector = ".section-full-width-banner video[autoplay]";
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isSafari = /Safari/.test(ua) && !/Chrome|Chromium|CriOS|FxiOS|Edg|OPR|Android/.test(ua);
  const shouldPatchSafariMedia = isIOS || isSafari || window.Shopify?.designMode;

  if (!shouldPatchSafariMedia) {
    return;
  }

  let restoredFromHistory = false;
  let observer = null;

  function isNearViewport(video) {
    const style = window.getComputedStyle(video);
    const rect = video.getBoundingClientRect();
    const margin = 200;

    return video.offsetParent !== null
      && style.display !== "none"
      && style.visibility !== "hidden"
      && rect.width > 0
      && rect.height > 0
      && rect.bottom >= -margin
      && rect.top <= window.innerHeight + margin
      && rect.right >= -margin
      && rect.left <= window.innerWidth + margin;
  }

  function canAutoplay(video) {
    return video instanceof HTMLVideoElement
      && video.isConnected
      && video.matches(videoSelector)
      && !document.hidden
      && isNearViewport(video)
      && !video.closest(".media--with-controls");
  }

  function prepareVideo(video) {
    video.autoplay = true;
    video.loop = true;
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute("muted", "");
    video.setAttribute("playsinline", "");
  }

  function safePlay(video) {
    const playPromise = video.play();

    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch(() => {});
    }
  }

  function nudgeVideo(video) {
    if (!Number.isFinite(video.duration) || video.duration <= 0.2 || video.readyState < 2) {
      return false;
    }

    try {
      const nextTime = Math.min((video.currentTime || 0) + 0.08, video.duration - 0.05);
      video.currentTime = Math.max(0, nextTime);
      return true;
    } catch (error) {
      return false;
    }
  }

  function reloadVideo(video) {
    try {
      video.load();
    } catch (error) {
      return;
    }

    if (video.readyState >= 2) {
      safePlay(video);
      return;
    }

    video.addEventListener("loadeddata", () => safePlay(video), { once: true });
  }

  function resumeVideo(video, aggressive = false) {
    if (!canAutoplay(video)) {
      return;
    }

    prepareVideo(video);
    const startTime = video.currentTime || 0;
    safePlay(video);

    if (!aggressive) {
      return;
    }

    window.setTimeout(() => {
      if (!canAutoplay(video)) {
        return;
      }

      const moved = Math.abs((video.currentTime || 0) - startTime) > 0.04;
      if (!video.paused && moved) {
        return;
      }

      nudgeVideo(video);
      safePlay(video);

      const retryTime = video.currentTime || 0;
      window.setTimeout(() => {
        if (!canAutoplay(video)) {
          return;
        }

        const movedAfterRetry = Math.abs((video.currentTime || 0) - retryTime) > 0.04;
        if (video.paused || !movedAfterRetry) {
          reloadVideo(video);
        }
      }, 600);
    }, 600);
  }

  function syncVideos(root = document, aggressive = false) {
    root.querySelectorAll(videoSelector).forEach(video => {
      resumeVideo(video, aggressive || restoredFromHistory);

      if (!("IntersectionObserver" in window)) {
        return;
      }

      if (!observer) {
        observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              resumeVideo(entry.target, restoredFromHistory);
            }
          });
        }, { rootMargin: "200px 0px", threshold: 0.01 });
      }

      if (video.dataset.fullWidthBannerVideoObserved !== "true") {
        observer.observe(video);
        video.dataset.fullWidthBannerVideoObserved = "true";
      }
    });
  }

  function scheduleSync(aggressive = false) {
    window.setTimeout(() => {
      syncVideos(document, aggressive);
      requestAnimationFrame(() => syncVideos(document, aggressive));
    }, 250);
  }

  window.addEventListener("pageshow", event => {
    restoredFromHistory = event.persisted
      || performance.getEntriesByType?.("navigation")?.[0]?.type === "back_forward";

    scheduleSync(restoredFromHistory);
  });

  window.addEventListener("pagehide", () => {
    document.querySelectorAll(videoSelector).forEach(video => {
      if (video instanceof HTMLVideoElement && isNearViewport(video)) {
        video.pause();
      }
    });
  });

  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) {
      scheduleSync(restoredFromHistory);
    }
  });

  document.addEventListener("shopify:section:load", event => {
    syncVideos(event.target, true);
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => syncVideos());
  } else {
    syncVideos();
  }
})();